import React, { useMemo } from "react";
import { useShow } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import {
  Card,
  Descriptions,
  Row,
  Col,
  Table,
  Tag,
  Space,
} from "antd";
import dayjs from "dayjs";
import {
   RECEIVING_BY_ID_QUERY,
 } from "./queries";
import { ReceivingStatus, ReceivingItem, Attachment } from "./interface";
import { SubmitButton } from "./components/SubmitButton";
import { FilePdfOutlined, FileImageOutlined, FileTextOutlined, FileOutlined } from "@ant-design/icons";
import { UserInfo } from "../../components/UserInfo";

const StatusBadge: React.FC<{ status: ReceivingStatus }> = ({ status }) => {
  const colorMap: Record<ReceivingStatus, string> = {
    [ReceivingStatus.DRAFT]: "default",
    [ReceivingStatus.SUBMITTED_RECEIVED]: "orange",
    [ReceivingStatus.RECEIVED]: "processing",
    [ReceivingStatus.DRAFT_ACCEPTANCE]: "blue",
    [ReceivingStatus.SUBMITTED_ACCEPTANCE]: "cyan",
    [ReceivingStatus.ACCEPTED]: "success",
    [ReceivingStatus.PARTIAL_ACCEPTED]: "warning",
    [ReceivingStatus.REJECTED]: "error",
  };
  return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
};

// Helper function to get appropriate icon based on mimeType
const getMimeTypeIcon = (mimeType: string) => {
  if (mimeType.includes("pdf")) {
    return <FilePdfOutlined />;
  } else if (mimeType.includes("image")) {
    return <FileImageOutlined />;
  } else if (mimeType.includes("text")) {
    return <FileTextOutlined />;
  }
  return <FileOutlined />;
};

// Helper function to format file size
const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const ShowReceiving: React.FC = () => {
  const { query } = useShow({
    resource: "receivings",
    meta: {
      gqlQuery: RECEIVING_BY_ID_QUERY,
    },
  });

  const { data, isLoading } = query;
  const record = data?.data;


  const totalReceivedValue = useMemo(() => {
    if (!record?.items?.length) return 0;
    return record.items.reduce((s: number, it: ReceivingItem) =>
      s + (Number(it.unitPrice || 0) * Number(it.receivedQty || 0)), 0);
  }, [record?.items]);

  const totalAcceptedValue = useMemo(() => {
    if (!record?.items?.length) return 0;
    return record.items.reduce((s: number, it: ReceivingItem) =>
      s + (Number(it.unitPrice || 0) * Number(it.acceptedQty || 0)), 0);
  }, [record?.items]);
const totalOrderValue = useMemo(() => {
  if (!record?.items?.length) return 0;
  return record.items.reduce((s: number, it: ReceivingItem) =>
    s + (Number(it.unitPrice || 0) * Number(it.orderedQty || 0)), 0);
}, [record?.items]);

const totalValue = useMemo(() => {
  if (!record?.items?.length) return 0;
  if (record.status === ReceivingStatus.DRAFT_ACCEPTANCE || record.status === ReceivingStatus.ACCEPTED) {
    return record.items.reduce((s: number, it: ReceivingItem) =>
      s + (Number(it.unitPrice || 0) * Number(it.acceptedQty || 0)), 0);
  } else {
    return record.items.reduce((s: number, it: ReceivingItem) =>
      s + (Number(it.unitPrice || 0) * Number(it.receivedQty || 0)), 0);
  }
}, [record?.items, record?.status]);

const itemColumns = [
    { title: "Name", dataIndex: "name" },
    { title: "Description", dataIndex: "description" },
    { title: "Ordered Qty", dataIndex: "orderedQty" },
    { title: "Received Qty", dataIndex: "receivedQty" },
    ...(record?.status !== ReceivingStatus.DRAFT && record?.status !== ReceivingStatus.SUBMITTED_RECEIVED && record?.status !== ReceivingStatus.RECEIVED ? [
      { title: "Accepted Qty", dataIndex: "acceptedQty" },
      { title: "Rejected Qty", dataIndex: "rejectedQty" },
    ] : []),
    { title: "Unit", dataIndex: "unit" },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      render: (v: number) => `ETB ${Number(v || 0).toLocaleString()}`,
    },
    ...(record?.status !== ReceivingStatus.DRAFT && record?.status !== ReceivingStatus.SUBMITTED_RECEIVED && record?.status !== ReceivingStatus.RECEIVED ? [
      { title: "Remarks", dataIndex: "remarks" },
    ] : []),
  ];


  const filePreviewUrl = (fileId: string) => {
    try {
      const apiUrl = new URL((window as any).API_URL); // ?? "https://buyapi.truwrk.lo/graphql");
      return `${apiUrl.origin}/file-storage/preview/${fileId}`;
    } catch {
      return `/file-storage/preview/${fileId}`;
    }
  };

  return (
    <Show
      isLoading={isLoading}
      title={record ? `Receiving • ${record.receivingNo}` : "Receiving"}
      headerButtons={
        record && (
          <Space>
            {record.status === ReceivingStatus.DRAFT && (
              <SubmitButton
                receivingId={record.id as string}
                receiving={record}
                fiscalYear={new Date().getFullYear()}
                buttonText="Submit as Received"
                onSuccess={() => query.refetch()}
              />
            )}
            {record.status === ReceivingStatus.DRAFT_ACCEPTANCE && (
              <SubmitButton
                receivingId={record.id as string}
                receiving={record}
                fiscalYear={new Date().getFullYear()}
                buttonText="Submit Accepted"
                onSuccess={() => query.refetch()}
              />
            )}
          </Space>
        )
      }
    >
      {record && (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <Descriptions column={2} layout="vertical" bordered>
                  <Descriptions.Item label="Receiving No">{record.receivingNo}</Descriptions.Item>
                  <Descriptions.Item label="Status"><StatusBadge status={record.status} /></Descriptions.Item>
                  <Descriptions.Item label="PO No">{record.poNo || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Delivery Date">{record.deliveryDate ? dayjs(record.deliveryDate).format("YYYY-MM-DD") : ""}</Descriptions.Item>
                  <Descriptions.Item label="Expected Delivery Date">{record.expectedDeliveryDate ? dayjs(record.expectedDeliveryDate).format("YYYY-MM-DD") : "-"}</Descriptions.Item>
                  <Descriptions.Item label="Vendor">{record.vendorInfo?.companyName || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Vendor Contact">{record.vendorInfo?.contactPerson || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Vendor Email">{record.vendorInfo?.email || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Total Order Value">ETB {Number(totalOrderValue || 0).toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Total Value">ETB {Number(totalValue || 0).toLocaleString()}</Descriptions.Item>
                  {(record.status === ReceivingStatus.ACCEPTED || record.status === ReceivingStatus.PARTIAL_ACCEPTED) && (
                    <Descriptions.Item label="Total Accepted Value">ETB {Number(totalAcceptedValue || 0).toLocaleString()}</Descriptions.Item>
                  )}
                   <Descriptions.Item label="Received By">
                     {record.receivedBy ? <UserInfo userId={record.receivedBy} /> : "-"}
                   </Descriptions.Item>
                   {(record.status === ReceivingStatus.ACCEPTED || record.status === ReceivingStatus.PARTIAL_ACCEPTED) && (
                     <>
                       <Descriptions.Item label="Quality Check By">
                         {record.qualityCheckBy ? <UserInfo userId={record.qualityCheckBy} /> : "-"}
                       </Descriptions.Item>
                      <Descriptions.Item label="Quality Check Date">{record.qualityCheckDate ? dayjs(record.qualityCheckDate).format("YYYY-MM-DD") : "-"}</Descriptions.Item>
                      <Descriptions.Item label="Quality Check Result">{record.qualityCheckResult || "-"}</Descriptions.Item>
                      <Descriptions.Item label="Inspection Date">{record.inspectionDate ? dayjs(record.inspectionDate).format("YYYY-MM-DD") : "-"}</Descriptions.Item>
                       <Descriptions.Item label="Accepted By">
                         {record.acceptedBy ? <UserInfo userId={record.acceptedBy} /> : "-"}
                       </Descriptions.Item>
                      <Descriptions.Item label="Accepted Date">{record.acceptedDate ? dayjs(record.acceptedDate).format("YYYY-MM-DD") : "-"}</Descriptions.Item>
                      <Descriptions.Item label="Inspection Notes" span={2}>{record.inspectionNotes || "-"}</Descriptions.Item>
                    </>
                  )}
                  <Descriptions.Item label="Notes" span={2}>{record.notes || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Delivery Notes" span={2}>{record.deliveryNotes || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Tags">{(record.tags || []).join(", ") || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Created At">{dayjs(record.createdAt).format("YYYY-MM-DD HH:mm")}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Items">
                <Table
                  dataSource={record.items}
                  columns={itemColumns as any}
                  rowKey={(r: any) => r.id}
                  pagination={false}
                  summary={() => {
                    const baseColumnsCount = 6; // Name, Description, Ordered Qty, Received Qty, Unit, Unit Price
                    const conditionalColumnsCount = record?.status !== ReceivingStatus.DRAFT && record?.status !== ReceivingStatus.SUBMITTED_RECEIVED && record?.status !== ReceivingStatus.RECEIVED ? 2 : 0; // Accepted Qty, Rejected Qty
                    const remarksColumnCount = record?.status !== ReceivingStatus.DRAFT && record?.status !== ReceivingStatus.SUBMITTED_RECEIVED && record?.status !== ReceivingStatus.RECEIVED ? 1 : 0; // Remarks
                    const totalColumns = baseColumnsCount + conditionalColumnsCount + remarksColumnCount;

                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={totalColumns - 1}><strong>Total</strong></Table.Summary.Cell>
                        <Table.Summary.Cell index={1}><strong>ETB {Number(totalValue || 0).toLocaleString()}</strong></Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Attachments">
                <Space wrap>
                  {(record.attachments || []).map((attachment: Attachment) => {
                    return (
                      <div key={attachment.fileId} style={{ border: "1px solid #eee", padding: 8, borderRadius: 6 }}>
                        <Space>
                          {getMimeTypeIcon(attachment.mimeType)}
                          <a href={filePreviewUrl(attachment.fileId)} target="_blank" rel="noreferrer">
                            {attachment.originalName} {attachment.size > 0 && `(${formatBytes(attachment.size)})`}
                          </a>
                        </Space>
                      </div>
                    );
                  })}
                  {!record.attachments?.length && <div>No attachments</div>}
                  <div style={{ marginTop: 8, color: "#888" }}>
                    Upload/remove is available on Edit page.
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

        </>
      )}
    </Show>
  );
};